import type { FC } from 'react'
import { useState } from 'react'

import { MessagePopup } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'

import { CustomerSelection } from './CustomerSelection'
import { TermsOfUse } from './TermsOfUse'

import adminQueries from '../../queries'
import type { Customer, Feature } from '../../resources'
import adminResources from '../../resources'

type VisibleContent = 'customerSelection' | 'termsOfUse' | 'activationHistory'

export const FeatureModal: FC<{
  feature: Feature
  customers: Customer[]
  selectedCustomerId: number | null
  onClose: () => void
}> = ({ feature, customers, selectedCustomerId: preSelectedCustomerId, onClose }) => {
  const [visibleContent, setVisibleContent] = useState<VisibleContent>(
    preSelectedCustomerId ? 'termsOfUse' : 'customerSelection'
  )
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    preSelectedCustomerId || (customers.length === 1 ? customers[0].id : null)
  )

  const { data: featureDetails, isLoading } = useQuery(
    adminQueries.adminFeatureDetails(feature.id),
    () => adminResources.getFeatureDetails(feature.id)
  )

  function handleClose() {
    onClose()
    setVisibleContent('customerSelection')
    setSelectedCustomerId(null)
  }

  const selectedCustomerActivationHistory = featureDetails?.customerFeatures.find(
    ({ customerId: featureCustomerId }) => featureCustomerId === selectedCustomerId
  )?.logs

  const renderTitle = () => {
    if (visibleContent === 'termsOfUse') return <Translation id="Terms & Conditions" />

    if (visibleContent === 'activationHistory' && selectedCustomerId)
      return (
        <Translation
          id="Activation and deactivation log for: {customer}"
          customer={customers.find(item => item.id === selectedCustomerId)?.name || ''}
        />
      )

    return <Translation id="Select company" />
  }

  return (
    <MessagePopup
      type="success"
      variant={visibleContent === 'customerSelection' ? 'wide' : 'medium'}
      isOpen={true}
      onClose={handleClose}
      // Use info from the list to show the title right away
      title={renderTitle()}
    >
      {visibleContent === 'customerSelection' && (
        <CustomerSelection
          customerFeatures={featureDetails?.customerFeatures}
          selectedCustomerId={selectedCustomerId}
          isSelectedCustomerActivationHistory={Boolean(selectedCustomerActivationHistory)}
          isDataLoading={isLoading}
          onSetSelectedCustomerId={setSelectedCustomerId}
          onClose={handleClose}
          onContinue={() => setVisibleContent('termsOfUse')}
          onSeeActivationHistory={() => setVisibleContent('activationHistory')}
        />
      )}

      {visibleContent === 'termsOfUse' && selectedCustomerId && featureDetails && (
        <TermsOfUse
          feature={featureDetails}
          selectedCustomerActivationHistory={selectedCustomerActivationHistory}
          selectedCustomerId={selectedCustomerId}
          onGoBack={() => setVisibleContent('customerSelection')}
          onClose={handleClose}
        />
      )}
    </MessagePopup>
  )
}
